import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {OptionsChainService} from '../option-chains.service';
import {isVoid} from '../utils';
import {parseOptionTicker, makeOptionTickerDisplayNameVerbose} from '../options-common/options.model';
import {PHA_SIDES, PhaSide, PhaTotals, PhaUnderlyingRow} from './pha-sheet.model';
import {PhaRowMark} from './session/analysis-changes.builder';

// THE UNDERLYINGS TABLE (CR-20260929-001 M1b). A custom row-based grid in the hedge pad /
// multi-trade pad style — NOT ag-Grid (owner ruling 2026-09-29: input-and-manipulation tables are
// custom rows; ag-Grid is for representation). Columns per system-description.md §4 (the selection
// checkbox column removed 2026-09-29 smoke 2, "not needed yet"): side → type → underlying → shares [lock] → price → $ value → % of portfolio [lock] → beta → beta-Δ →
// remove (X, the far-right last column). Totals are the table's own last row.
//
// DUMB BY DESIGN: it renders what it is given and emits what the user did. The sheet model owns
// every rule; the panel owns the sheet. OnPush: the panel hands over a NEW rows array and a NEW
// totals object after every recompute, which is what makes this component repaint.

@Component({
    selector: 'ets-pha-underlyings-table',
    templateUrl: './pha-underlyings-table.component.html',
    styleUrls: ['./pha-underlyings-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaUnderlyingsTableComponent {
    constructor(private readonly _chains: OptionsChainService) {}

    @Input() rows: PhaUnderlyingRow[] = [];

    @Input() totals: PhaTotals = null;

    /** The account-size gate: false = every input in the table is disabled. */
    @Input() unlocked = false;

    /** The session's marks per row id (CR-20260930-001 M2): changed cells, the lock, the block. A new object on every change. */
    @Input() marks: {[rowId: number]: PhaRowMark} = {};

    /** Copy the row's order block to ToS (a stock is its own block). */
    @Output() copyClicked = new EventEmitter<PhaUnderlyingRow>();
    /** Revoke the row's copied block (back to not-exported). */
    @Output() revokeClicked = new EventEmitter<PhaUnderlyingRow>();

    markOf(row: PhaUnderlyingRow): PhaRowMark | null {
        return (this.marks && this.marks[row.id]) || null;
    }

    isExpired(row: PhaUnderlyingRow): boolean { return row.assetType === 'Call' && this._chains.isOptionExpired(row.ticker); }

    isLocked(row: PhaUnderlyingRow): boolean {
        const m = this.markOf(row);
        return !!m && m.locked;
    }

    isOverdue(row: PhaUnderlyingRow): boolean {
        const m = this.markOf(row);
        return !!m && m.overdue;
    }

    canCopy(row: PhaUnderlyingRow): boolean {
        const m = this.markOf(row);
        return !this.isExpired(row) && !!m && m.changed && m.status === 'Pending';
    }

    /** The before value of a changed cell, as text; null when the cell is unchanged. */
    beforeOf(row: PhaUnderlyingRow, cell: 'shares'): string | null {
        const m = this.markOf(row);
        const v = m && m.before ? m.before[cell] : undefined;
        return v === undefined ? null : v;
    }

    @Output() sharesTyped = new EventEmitter<{row: PhaUnderlyingRow, shares: number}>();
    @Output() percentTyped = new EventEmitter<{row: PhaUnderlyingRow, pct: number}>();
    @Output() sharesLockToggled = new EventEmitter<PhaUnderlyingRow>();
    @Output() pctLockToggled = new EventEmitter<PhaUnderlyingRow>();
    @Output() removeClicked = new EventEmitter<PhaUnderlyingRow>();
    @Output() restoreClicked = new EventEmitter<PhaUnderlyingRow>();
    /** The row's SIDE picked from its dropdown (item 4). */
    @Output() sideChanged = new EventEmitter<{row: PhaUnderlyingRow, side: PhaSide}>();

    readonly centered = {style: 'text-align: center;'};
    readonly sides = PHA_SIDES;

    trackRow(_: number, row: PhaUnderlyingRow): number {
        return row.id;
    }

    onSideChanged(row: PhaUnderlyingRow, ev: {value: PhaSide, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event) || !ev.value || ev.value === row.side) {
            return;
        }
        this.sideChanged.emit({row, side: ev.value});
    }

    // dx-number-box fires onValueChanged for programmatic writes too (event undefined); only a
    // user's edit is a typed value (the Hedge Matrix zoom handler makes the same distinction).
    onSharesChanged(row: PhaUnderlyingRow, ev: {value: number, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        if (ev.value === null || ev.value === undefined || isNaN(ev.value) || ev.value < 0) {
            return;
        }
        this.sharesTyped.emit({row, shares: ev.value});
    }

    onPercentChanged(row: PhaUnderlyingRow, ev: {value: number, event?: any}): void {
        if (this.isExpired(row) || isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        if (ev.value === null || ev.value === undefined || isNaN(ev.value) || ev.value < 0) {
            return;
        }
        this.percentTyped.emit({row, pct: ev.value});
    }

    displayOf(row: PhaUnderlyingRow): string {
        if (row.assetType === 'Call') {
            const option = parseOptionTicker(row.ticker);
            return option ? (this.isExpired(row) ? `${option.underlying} ${option.type} ${option.strike} Expired` : makeOptionTickerDisplayNameVerbose(option)) : row.displayName;
        }
        return row.displayName || row.ticker;
    }
}
